import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';

@Component({
  selector: 'app-api-docs',
  templateUrl: './api-docs.component.html',
  styleUrls: ['./api-docs.component.scss'],
})
export class ApiDocsComponent implements OnInit {
  sections: any[] = [];
  loading = true;
  error = '';
  expandedSections: Set<number> = new Set();
  expandedEndpoints: Map<string, Set<number>> = new Map();
  inputViewMode: Map<string, 'schema' | 'example'> = new Map();
  outputViewMode: Map<string, 'schema' | 'example'> = new Map();
  expandedExamples: Map<string, Set<string>> = new Map();
  allErrorCategoriesExpanded: boolean = false;

  constructor(private http: HttpClient) {}

  ngOnInit() {
    this.loadEndpoints();
    this.initializeExpandedCategories();
  }

  initializeExpandedCategories() {
    // Collapse all error categories by default
    const categorySet = new Set<string>();
    this.expandedExamples.set('-1', categorySet);
  }

  loadEndpoints() {
    this.http.get<any>('/api/v1/api-docs/endpoints.json')
      .subscribe({
        next: (data) => {
          // Store sections directly instead of flattening
          this.sections = data && data.sections ? data.sections : [];
          this.loading = false;
        },
        error: (err) => {
          this.error = 'Failed to load API documentation';
          this.loading = false;
        },
      });
  }

  getErrorsArray(errors: any): any[] {
    if (!errors) return [];

    return Object.keys(errors).map(key => ({
      code: key,
      message: errors[key].message || errors[key],
    }));
  }

  // Section-level methods
  toggleSection(sectionIndex: number): void {
    if (this.expandedSections.has(sectionIndex)) {
      this.expandedSections.delete(sectionIndex);
    } else {
      this.expandedSections.add(sectionIndex);
    }
  }

  isSectionExpanded(sectionIndex: number): boolean {
    return this.expandedSections.has(sectionIndex);
  }

  // Endpoint-level methods (now with section context)
  toggleEndpoint(sectionIndex: number, endpointIndex: number): void {
    const sectionKey = sectionIndex.toString();
    if (!this.expandedEndpoints.has(sectionKey)) {
      this.expandedEndpoints.set(sectionKey, new Set());
    }

    const endpointSet = this.expandedEndpoints.get(sectionKey)!;
    if (endpointSet.has(endpointIndex)) {
      endpointSet.delete(endpointIndex);
    } else {
      endpointSet.add(endpointIndex);
    }
  }

  isEndpointExpanded(sectionIndex: number, endpointIndex: number): boolean {
    const sectionKey = sectionIndex.toString();
    const endpointSet = this.expandedEndpoints.get(sectionKey);
    return endpointSet ? endpointSet.has(endpointIndex) : false;
  }

  // Helper method to generate unique key for endpoints
  getEndpointKey(sectionIndex: number, endpointIndex: number): string {
    return `${sectionIndex}-${endpointIndex}`;
  }

  // Update existing methods to work with section context
  toggleInputView(sectionIndex: number, endpointIndex: number): void {
    const key = this.getEndpointKey(sectionIndex, endpointIndex);
    const current = this.inputViewMode.get(key) || 'schema';
    this.inputViewMode.set(key, current === 'schema' ? 'example' : 'schema');
  }

  isInputSchemaView(sectionIndex: number, endpointIndex: number): boolean {
    const key = this.getEndpointKey(sectionIndex, endpointIndex);
    return this.inputViewMode.get(key) !== 'example';
  }

  toggleOutputView(sectionIndex: number, endpointIndex: number): void {
    const key = this.getEndpointKey(sectionIndex, endpointIndex);
    const current = this.outputViewMode.get(key) || 'schema';
    this.outputViewMode.set(key, current === 'schema' ? 'example' : 'schema');
  }

  isOutputSchemaView(sectionIndex: number, endpointIndex: number): boolean {
    const key = this.getEndpointKey(sectionIndex, endpointIndex);
    return this.outputViewMode.get(key) !== 'example';
  }

  toggleExample(sectionIndex: number, endpointIndex: number, exampleType: string): void {
    const key = this.getEndpointKey(sectionIndex, endpointIndex);
    if (!this.expandedExamples.has(key)) {
      this.expandedExamples.set(key, new Set());
    }

    const exampleSet = this.expandedExamples.get(key)!;
    if (exampleSet.has(exampleType)) {
      exampleSet.delete(exampleType);
    } else {
      exampleSet.add(exampleType);
    }
  }

  isExampleExpanded(sectionIndex: number, endpointIndex: number, exampleType: string): boolean {
    const key = this.getEndpointKey(sectionIndex, endpointIndex);
    const exampleSet = this.expandedExamples.get(key);
    return exampleSet ? exampleSet.has(exampleType) : false;
  }

  getErrorKeys(errors: any): string[] {
    if (!errors) return [];
    return Object.keys(errors);
  }

  getErrorExamples(errors: any): any[] {
    if (!errors) return [];

    return Object.keys(errors).map(key => ({
      code: key,
      message: errors[key].message || errors[key],
      example: this.generateErrorExample(key, errors[key]),
      category: this.getErrorCategory(key),
      severity: this.getErrorSeverity(key),
      httpStatus: this.getHttpStatusForError(key),
    }));
  }

  getCategorizedErrors(errors: any): any {
    const errorExamples = this.getErrorExamples(errors);

    const categorized = {
      validation: [],
      ui_restrictions: [],
      configuration: [],
      system: [],
    };

    errorExamples.forEach(error => {
      const category = error.category;
      if (categorized[category]) {
        categorized[category].push(error);
      }
    });

    return categorized;
  }

  toggleErrorCategory(category: string): void {
    const categorySet = this.expandedExamples.get('-1') || new Set();
    if (categorySet.has(category)) {
      categorySet.delete(category);
    } else {
      categorySet.add(category);
    }
    this.expandedExamples.set('-1', categorySet);
    this.updateAllCategoriesExpandedState();
  }

  isCategoryExpanded(category: string): boolean {
    const categorySet = this.expandedExamples.get('-1') || new Set();
    return categorySet.has(category);
  }

  getErrorCategory(errorKey: string): string {
    const uiRestrictions = ['ui_restriction_ias', 'ui_restriction_iasgroups', 'ui_restriction_keywords',
      'ui_no_feature_groups', 'ui_missing_includes', 'ui_missing_config', 'ui_invalid_config'];
    const validationErrors = ['validation', 'feature_group_missing_include', 'invalid_created_from'];
    const configErrors = ['json_decode', 'max_feature_groups', 'api_config_required', 'api_feature_group_invalid'];
    const systemErrors = ['duplicate', 'server'];

    if (uiRestrictions.includes(errorKey)) return 'ui_restrictions';
    if (validationErrors.includes(errorKey)) return 'validation';
    if (configErrors.includes(errorKey)) return 'configuration';
    if (systemErrors.includes(errorKey)) return 'system';
    return 'system';
  }

  getErrorSeverity(errorKey: string): string {
    const highSeverity = ['server', 'json_decode'];
    const mediumSeverity = ['validation', 'duplicate', 'max_feature_groups'];
    if (highSeverity.includes(errorKey)) return 'high';
    if (mediumSeverity.includes(errorKey)) return 'medium';
    return 'low';
  }

  getHttpStatusForError(errorKey: string): number {
    const status400 = ['validation', 'feature_group_missing_include', 'json_decode', 'max_feature_groups',
      'api_config_required', 'api_feature_group_invalid', 'ui_no_feature_groups', 'ui_missing_includes',
      'ui_restriction_ias', 'ui_restriction_iasgroups', 'ui_restriction_keywords', 'ui_missing_config',
      'ui_invalid_config', 'invalid_created_from'];
    const status409 = ['duplicate'];
    const status500 = ['server'];

    if (status400.includes(errorKey)) return 400;
    if (status409.includes(errorKey)) return 409;
    if (status500.includes(errorKey)) return 500;
    return 400;
  }

  toggleAllErrorCategories(): void {
    const allCategories = ['validation', 'ui_restrictions', 'configuration', 'system'];
    if (this.allErrorCategoriesExpanded) {
      // Collapse all
      this.expandedExamples.set('-1', new Set());
      this.allErrorCategoriesExpanded = false;
    } else {
      // Expand all
      this.expandedExamples.set('-1', new Set(allCategories));
      this.allErrorCategoriesExpanded = true;
    }
  }

  updateAllCategoriesExpandedState(): void {
    const categorySet = this.expandedExamples.get('-1') || new Set();
    const allCategories = ['validation', 'ui_restrictions', 'configuration', 'system'];
    this.allErrorCategoriesExpanded = allCategories.every(category => categorySet.has(category));
  }

  generateErrorExample(errorKey: string, errorData: any): any {
    const errorMappings = {
      validation: 'name is required and must be at least 5 characters',
      json_decode: 'invalid request payload',
      max_feature_groups: 'maximum of 10 FeatureGroups allowed',
      api_config_required: 'API segments require at least one value in IncludeFeatureSets, IAS, IASGroups or Keywords',
      api_feature_group_invalid: 'featureGroups validation failed: Ensure all FeatureGroups have valid ' +
        'IncludeFeatureSets and configurations',
      ui_no_feature_groups: 'UI segments require at least one "Include" FeatureGroup',
      ui_missing_includes: 'UI segments require at least one IncludeFeatureSet',
      ui_restriction_ias: 'IAS is not allowed for UI segments',
      ui_restriction_iasgroups: 'IASGroups is not allowed for UI segments',
      ui_restriction_keywords: 'keywords is not allowed for UI segments',
      ui_missing_config: 'UI segments require Config and at least one include FeatureGroups',
      ui_invalid_config: 'invalid config',
      invalid_created_from: 'invalid createdFrom value: must be either \'API\' or \'UI\'',
      feature_group_missing_include: 'each feature group must contain at least one include feature set',
      duplicate: 'A segment with this configuration already exists',
      server: 'Internal server error occurred while processing request',
    };

    return {
      message: errorMappings[errorKey] || errorData.message || 'Unknown error',
      example: {
        error: errorMappings[errorKey] || errorData.message || 'Unknown error',
        code: errorKey,
        timestamp: new Date().toISOString(),
      },
    };
  }

  generateInputExample(endpoint: any): any {
    if (!endpoint.input) return {};

    const example = {};
    Object.keys(endpoint.input).forEach(key => {
      const value = endpoint.input[key];
      if (typeof value === 'string') {
        if (value.includes('string')) {
          if (key === 'name') example[key] = 'Example Segment Name';
          else if (key === 'createdFrom') example[key] = 'API';
          else if (key === 'description') example[key] = 'Example description';
          else if (key === 'CtxSegmentUUID') example[key] = 'gYDq';
          else example[key] = 'example_string';
        } else if (value.includes('int')) {
          // Generate a random integer between 1 and 10
          example[key] = Math.floor(Math.random() * 10) + 1;
        } else {
          example[key] = 'example_value';
        }
      } else if (typeof value === 'object') {
        example[key] = this.generateObjectExample(value);
      }
    });

    return example;
  }

  generateObjectExample(obj: any): any {
    // If schema provides an array, generate one item from its first element schema
    if (Array.isArray(obj)) {
      if (obj.length === 0) return [];
      return [this.generateObjectExample(obj[0])];
    }

    // Primitive hint strings like "int (...)", "float (...)", "boolean (...)" etc.
    if (typeof obj === 'string') {
      const s = obj.toLowerCase();
      if (s.includes('int')) return 1;
      if (s.includes('float') || s.includes('double') || s.includes('number')) return 1;
      if (s.includes('boolean')) return true;
      if (s.includes('timestamp') || s.includes('iso8601') || s.includes('date')) return new Date().toISOString();
      return 'example_value';
    }

    // Leave real primitives unchanged
    if (typeof obj !== 'object' || obj === null) return obj;

    // Objects
    const example: any = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];

      if (Array.isArray(value)) {
        example[key] = value.length > 0 ? [this.generateObjectExample(value[0])] : [];
        return;
      }

      if (typeof value === 'string') {
        const s = value.toLowerCase();
        if (key === 'iabid')        { example[key] = 1; return; }
        if (key === 'version')      { example[key] = 2.0; return; }
        if (key === 'iabname')      { example[key] = 'Automotive'; return; }
        if (key === 'tier')         { example[key] = 1; return; }
        if (key === 'parentid')     { example[key] = null; return; }

        if (s.includes('int'))      { example[key] = 1; return; }
        if (s.includes('float') || s.includes('double') || s.includes('number')) { example[key] = 1; return; }
        if (s.includes('boolean'))  { example[key] = true; return; }
        if (s.includes('timestamp') || s.includes('iso8601') || s.includes('date')) { example[key] = new Date().toISOString(); return; }

        // named hints
        if (key.toLowerCase() === 'uuid')          { example[key] = 'abcd1234'; return; }
        if (key.toLowerCase() === 'name')          { example[key] = 'Example Name'; return; }
        if (key.toLowerCase() === 'description')   { example[key] = 'Example description'; return; }

        example[key] = 'example_value';
        return;
      }

      if (typeof value === 'object' && value !== null) {
        example[key] = this.generateObjectExample(value);
        return;
      }

      example[key] = value;
    });

    return example;
  }

  generateOutputExample(endpoint: any): any {
    if (endpoint.examples && typeof endpoint.examples === 'object') {
      // Return the first example found, regardless of its name
      const exampleKeys = Object.keys(endpoint.examples);
      if (exampleKeys.length > 0) {
        return endpoint.examples[exampleKeys[0]];
      }
    }
    return this.generateObjectExample(endpoint.output || {});
  }

  getSuccessExamples(examples: any): any[] {
    if (!examples) return [];

    const successExamples = [];
    Object.keys(examples).forEach(key => {
      if (key.startsWith('success_')) {
        successExamples.push({
          type: key,
          title: this.formatExampleTitle(key),
          data: examples[key],
        });
      }
    });

    return successExamples;
  }

  formatExampleTitle(exampleKey: string): string {
    return exampleKey.replace(/^success_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  getResponseExamples(responses: any): any[] {
    if (!responses) return [];

    return Object.keys(responses).map(status => ({
      status,
      statusClass: this.getStatusClass(status),
      description: this.getStatusDescription(status),
    }));
  }

  getStatusClass(status: string): string {
    const statusNum = parseInt(status, 10);
    if (statusNum >= 200 && statusNum < 300) return 'success';
    if (statusNum >= 300 && statusNum < 400) return 'redirect';
    if (statusNum >= 400 && statusNum < 500) return 'client-error';
    if (statusNum >= 500) return 'server-error';
    return 'unknown';
  }

  getStatusDescription(status: string): string {
    const {status: statusCode} = {status};
    const descriptions = {
      200: 'OK - Request successful',
      201: 'Created - Resource created successfully',
      400: 'Bad Request - Invalid request data',
      401: 'Unauthorized - Authentication required',
      403: 'Forbidden - Access denied',
      404: 'Not Found - Resource not found',
      409: 'Conflict - Resource already exists',
      500: 'Internal Server Error - Server error',
    };

    return descriptions[statusCode] || `HTTP ${statusCode}`;
  }

  formatJson(obj: any): string {
    if (typeof obj === 'string') {
      return obj;
    }
    return JSON.stringify(obj, null, 2);
  }

  copyToClipboard(text: string, event: Event): void {
    event.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      const button = event.target as HTMLButtonElement;
      const originalText = button.textContent;
      button.textContent = '✓ Copied!';
      setTimeout(() => { button.textContent = originalText; }, 2000);
    });
  }
}
